<template>
  <div class="stepper-form-container">
    <FlyoutMenuHead :selectedItem="selectedItem" route="operator-groups" :channel="null" class="ml-8" />

    <v-card flat class="form-card">
      <v-card-title></v-card-title>

      <ReusableStepper
        v-model="currentStep"
        :steps="steps"
        :width="900"
        :editable="true"
        :usePathIcon="false"
        :can-navigate="guardStepper"
      >
        <template #step="{ step }">
          <GroupMainInfo
            v-if="step === 1"
            ref="step1"
            :form="form"
            :errors="errors.creating || {}"
            @update:form="updateForm"
            @error="handleError"
            class="ml-8"
          />
          <GroupAddOperators
            v-else-if="step === 2"
            ref="step2"
            :form="form"
            :created-group-id="createdGroupId"
            @update:form="updateForm"
            @error="handleError"
          />
          <GroupPermissions
            class="ml-8"
            v-else-if="step === 3"
            ref="step3"
            :form="form"
            :groupUuid="createdGroupId"
            @update:form="updateForm"
            @error="handleError"
          />
        </template>
      </ReusableStepper>
    </v-card>
    <FormFooter
      :current-step="currentStep"
      :total-steps="steps.length"
      :next-disabled="!canProceedToNextStep"
      :loading="isCreatingGroup"
      @next="onNext"
      @prev="prevStep"
      @save="saveOperatorGroup"
      @cancel="handleCancel"
    />

  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import FlyoutMenuHead from "../FlyoutMenuHead.vue";
import ReusableStepper from "./ReusableStepper.vue";
import FormFooter from "./FormFooter.vue";
import GroupMainInfo from "./GroupMainInfo.vue";
import GroupAddOperators from "./GroupAddOperators.vue";
import GroupPermissions from "./GroupPermissions.vue";


export default {
  name: "CreateOperatorGroupForm",
  components: {
    FlyoutMenuHead,
    ReusableStepper,
    FormFooter,
    GroupMainInfo,
    GroupAddOperators,
    GroupPermissions,
    
  },
  props: {
    progress: {
      type: [String, Number],
      default: 1,
    },
  },
  data() {
    return {
      currentStep: parseInt(this.progress) || 1,
      watchers: [], // Track watchers for cleanup
      debounceTimers: new Map(), // Track debounce timers
      createdGroupId: null, // Store the created group ID from first step
      isCreatingGroup: false, // Track if group creation is in progress
      steps: [
        { title: "Main Info" },
        { title: "Operators" },
        { title: "Permissions" },
      ],
      form: {
        name: "",
        description: "",
        type: "manual", // manual or smart
        isSmartGroup: false,
        active: true,
        selectedOperators: [],
        permissions: {
          selectedClaims: [],
          groupStates: {},
        },
      },
    };
  },
  computed: {
    ...mapGetters({
      loading: "operators/isLoading",
      errors: "operators/getErrors",
      currentUser: "accounts/getUser", // Get current user info
    }),
    selectedItem() {
      return {
        name: "Create Operator Group",
        subtitle: `Step ${this.currentStep} of ${this.steps.length}`,
      };
    },
    guardStepper() {
      // Allow navigation between completed steps
      return true;
    },
    canProceedToNextStep() {
      switch (this.currentStep) {
        case 1:
          // Step 1: Validate main info (name is required)
          return this.form.name && 
                 this.form.name.trim().length >= 3 && 
                 this.form.name.length <= 100 &&
                 !this.isCreatingGroup;
        case 2:
          // Step 2: Operators are optional, so always allow proceeding
          return true;
        case 3:
          // Step 3: This is the final step, no "next" button
          return true;
        default:
          return false;
      }
    },
  },
  beforeDestroy() {
    // Clean up resources to prevent memory leaks
    this.cleanupForm();
  },
  methods: {
    ...mapActions({
      createOperatorGroup: "operators/createOperatorGroup",
      updateOperatorGroup: "operators/updateOperatorGroup",
      fetchPermissions: "operators/fetchPermissions",
      combinePermissionsForGroup: "operators/combinePermissionsForGroup",
      addAlert: "global/addAlert",
    }),
    updateForm(newData) {
      // Deep merge, but always preserve top-level fields (like name, description, type, active)
      function deepMerge(target, source) {
        for (const key in source) {
          if (
            source[key] &&
            typeof source[key] === 'object' &&
            !Array.isArray(source[key])
          ) {
            // Only deep merge for nested objects (permissions, etc.)
            target[key] = deepMerge(target[key] || {}, source[key]);
          } else {
            // For top-level fields, only overwrite if value is not undefined/null
            if (source[key] !== undefined && source[key] !== null) {
              target[key] = source[key];
            }
          }
        }
        return target;
      }
      // Always preserve top-level fields from existing form unless explicitly overwritten
      this.form = deepMerge(this.form, newData);
      // After merge, ensure group name is never lost
      if (!this.form.name || this.form.name.trim() === '') {
        if (newData.name && newData.name.trim() !== '') {
          this.form.name = newData.name;
        }
      }
    },
    async onNext() {
      const isValid = await this.validateCurrentStep();
      if (!isValid) {
        this.showAlert("Please fill in all required fields correctly.", "error");
        return;
      }

      // If we're on step 1, create the group first
      if (this.currentStep === 1) {
        await this.createGroupFromFirstStep().then (() => {
           this.addAlert({
              type: "success",
              message: "Group created successfully! Now add operators and permissions.",
            });
        }).catch((error) => {
           this.addAlert({
              type: "error",
              message: error.message || "Error creating group",
            });
        }); 
      } else {
        // If moving to step 3 (permissions), load all permissions with none selected
        if (this.currentStep === 2 && this.createdGroupId) {
          await this.loadPermissionsForGroup();
        }
        this.nextStep();
      }
    },
    async loadPermissionsForGroup() {
      console.log('[CreateGroup] loadPermissionsForGroup called, createdGroupId:', this.createdGroupId);
      try {
        const perms = await this.fetchPermissions();
        console.log('[CreateGroup] fetchPermissions result:', perms?.length, 'categories');
        const combined = await this.combinePermissionsForGroup({
          groupUuid: this.createdGroupId,
          groupClaims: [] // Empty array = all permissions shown, none selected
        });
        console.log('[CreateGroup] combinePermissionsForGroup result:', combined?.length, 'categories');
      } catch (e) {
        console.error('[CreateGroup] Error loading permissions:', e);
      }
    },
    nextStep() {
      if (this.currentStep < this.steps.length) {
        this.currentStep++;
        this.updateRoute();
      }
    },
    prevStep() {
      if (this.currentStep > 1) {
        this.currentStep--;
        this.updateRoute();
      }
    },
    updateRoute() {
      const newRoute = {
        name: "addOperatorGroup",
        params: { progress: this.currentStep },
      };
      this.$router.replace(newRoute);
    },
    async validateCurrentStep() {
      const stepRef = this.$refs[`step${this.currentStep}`];
      if (stepRef && typeof stepRef.validate === "function") {
        return await stepRef.validate();
      }
      return true;
    },
    async createGroupFromFirstStep() {
      if (this.isCreatingGroup || this.createdGroupId) {
        // Already created or in progress
        this.nextStep();
        return;
      }

      this.isCreatingGroup = true;
      
      try {
        // Create group with basic info from step 1 - matching the required API structure
        const basicGroupPayload = {
          name: this.form.name.trim(),
          description: this.form.description?.trim() || "",
          active: this.form.active !== false, // Default to true
          automatic_operator_group: this.form.isSmartGroup || false,
          type: "manual",
          created_by_email: this.currentUser?.email,
        };
        
        const response = await this.createOperatorGroup(basicGroupPayload);
        
        // Store the created group info from API response (handle different response formats)
        this.createdGroupId = response.uuid || response.operator_group_uuid || (response.data && (response.data.uuid || response.data.operator_group_uuid));
        console.log('[CreateGroup] createdGroupId:', this.createdGroupId);
        this.form.groupUuid = this.createdGroupId;
        this.nextStep();
        
      } catch (error) {
        console.error("Error creating group:", error);
        this.showAlert(`Error creating group: ${error.message || error}`, "error");
      } finally {
        this.isCreatingGroup = false;
      }
    },

   async saveOperatorGroup() {
      if (!this.createdGroupId) {
        this.showAlert("Error: No group ID found. Please restart the process.", "error");
        return;
      }

      // Ensure name is provided
      if (!this.form.name || !this.form.name.trim()) {
        this.showAlert('Name is required to save the operator group.', 'error');
        return;
      }
      try {
        // Build payload with all required fields and permission claim UUIDs
        const updatePayload = {
          name: this.form.name,
          description: this.form.description,
          active: this.form.active,
          automatic_operator_group: this.form.isSmartGroup || false,
          type: "manual",
          operator_permission_claim_uuids: (this.form.permissions && this.form.permissions.selectedClaims) ? this.form.permissions.selectedClaims : [],
        };
        // Log the payload for debugging
      

        await this.updateOperatorGroup({
          operator_group_uuid: this.createdGroupId,
          payload: updatePayload
        }).then(response => {
          this.addAlert({
            type: "success",
            message: response.message || "Group updated successfully!",
          });
        }).catch((error) => {
          console.error("Error updating group:", error);
          const msg = error?.response?.data?.message || error?.message || String(error);
          this.addAlert({
            type: "error",
            message: `Error saving group: ${msg}`,
          });
        });
        setTimeout(() => this.$router.push("/operator-groups"), 1500);
      } catch (error) {
        console.error("Error updating group:", error);
        const msg = error?.response?.data?.message || error?.message || String(error);
        this.addAlert({
          type: "error",
          message: `Error saving group: ${msg}`,
        });
      }
    },
    handleCancel() {
      this.$router.push("/operator-groups");
    },
    showAlert(text, type = 'success') {
      this.alert = { show: true, text, type };
    },
    cleanupForm() {
      // 1. Clean up watchers
      this.watchers.forEach(unwatch => {
        if (typeof unwatch === 'function') {
          unwatch();
        }
      });
      this.watchers = [];

      // 2. Clear debounce timers
      this.debounceTimers.forEach(timerId => clearTimeout(timerId));
      this.debounceTimers.clear();

      // 3. Reset form state
      this.form = this.getInitialForm();
      // Note: Don't reset computed properties like 'errors'
    },

    getInitialForm() {
      return {
        name: '',
        description: '',
        active: true,
        isSmartGroup: false,
        selectedOperators: [],
        permissions: {
          selectedClaims: [],
          groupStates: {},
        }
      };
    },
    handleError(errorMessage) {
      this.showAlert(errorMessage, "error");
    },
  },
  watch: {
    progress: {
      handler(newVal) {
        const step = parseInt(newVal) || 1;
        if (step !== this.currentStep) {
          this.currentStep = step;
        }
      },
      immediate: true,
    },
  },
};
</script>

<style scoped>
.stepper-form-container {
  width: 100%;
  height: 100vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.form-card {
  flex: 1;
  background: transparent !important;
  box-shadow: none !important;
}

.form-card .v-card__title {
  padding: 0;
}

</style>